import './belege.css';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fmtDayMonth, monthName, monthShort, plural, today } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { BelegDialog, CATEGORY_LABEL, istBankDatei } from './BelegDialog';
import type { Category, MonthState, YearResponse } from './types';

type Cell = { cls: string; label: string };

// Feld im Monatsraster wie in der Demo: gebucht / Nachtrag / Beleg fehlt / nachgereicht / hochgeladen / offen / –
function cell(state: MonthState, count: number): Cell {
  switch (state) {
    case 'gebucht':
      return { cls: 'ok', label: 'gebucht' };
    case 'nachtrag':
      return { cls: 'info', label: 'Nachtrag' };
    case 'fehlt':
      return { cls: 'bad', label: 'Beleg fehlt' };
    case 'nachgereicht':
      return { cls: 'info', label: 'nach­gereicht' };
    case 'hochgeladen':
      return { cls: 'info', label: 'hoch­geladen' };
    // Hier zählen nur Rechnungen aus dem Rechnungsprogramm (Hochgeladenes macht den Monat „hochgeladen“)
    case 'zukunft':
      return count ? { cls: 'info', label: 'Rech­nungen' } : { cls: 'neu', label: '–' };
    case 'vorher':
      return count ? { cls: 'info', label: 'Rech­nungen' } : { cls: 'oth', label: '–' };
    default:
      return { cls: 'warn', label: 'offen' };
  }
}

// Tab „Belege vom Mandanten“: Jahr wählen, Monat anklicken, Belege ansehen, Monat als gebucht setzen oder „Beleg fehlt“ melden.
// „Für DATEV exportieren“ lädt die Belege des Monats als ZIP-Datei mit einem Ordner je DATEV-Belegtyp.
// Jahr und Monat stehen in der Adresse (?jahr=2026&monat=9), damit „Ansehen“ aus den Hinweisleisten direkt dorthin führt.
export function BelegeTab({ clientId }: { clientId: string }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const t = today();
  const year = Number(params.get('jahr')) || t.year;
  const selM = Number(params.get('monat')) || null;
  const res = useApi<YearResponse>(`/api/kanzlei/belege/${clientId}/years/${year}`, { live: ['belege'], clientId });
  const [askMiss, setAskMiss] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const d = res.data;
  // ?beleg=<id> (aus der Glocke): Beleg-Fenster gleich öffnen
  const fromUrl = params.get('beleg');
  useEffect(() => {
    if (fromUrl) setOpen(fromUrl);
  }, [fromUrl]);
  const closeBeleg = () => {
    setOpen(null);
    if (fromUrl) {
      const next = new URLSearchParams(params);
      next.delete('beleg');
      setParams(next, { replace: true });
    }
  };

  const select = (y: number, m: number | null) => {
    const next = new URLSearchParams(params);
    next.set('jahr', String(y));
    if (m) next.set('monat', String(m));
    else next.delete('monat');
    setParams(next, { replace: true });
    setAskMiss(false);
  };

  const setStatus = async (action: 'gebucht' | 'fehlt' | 'reset', note?: string) => {
    if (!selM || !d) return;
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/belege/${clientId}/months/${year}/${selM}`, { action, note });
      await res.reload();
      setAskMiss(false);
      // Monat wie in der Demo; das Jahr nur, wenn es nicht das aktuelle ist
      const mn = year === t.year ? monthName(selM) : `${monthName(selM)} ${year}`;
      toast(
        action === 'gebucht'
          ? `${mn} als gebucht gesetzt · sichtbar in der App`
          : action === 'reset'
            ? `${mn} zurückgesetzt · sichtbar in der App`
            : `${mn}: Beleg fehlt · ${d.client.contactName} sieht den Hinweis`,
      );
    } catch (err) {
      toast(`Speichern fehlgeschlagen · ${errorText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const m = d && selM ? d.months[selM - 1] : undefined;
  const files = d && selM ? d.belege.filter((b) => b.month === selM) : [];
  const c = m ? cell(m.state, m.count) : null;

  return (
    <div className="card">
      <div className="ch">
        <h3>Belege {year}</h3>
        <span>Monat anklicken: Belege ansehen, als gebucht setzen oder Beleg fehlt melden</span>
      </div>
      <div className="yrow" role="group" aria-label="Jahr">
        {(d?.years ?? [year]).map((y) => (
          <button key={y} type="button" className={`btn sm${y === year ? ' dark' : ''}`} aria-pressed={y === year} onClick={() => select(y, null)}>
            {y}
          </button>
        ))}
      </div>
      {!d ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <div className="mgrid">
          {d.months.map((x) => {
            const k = cell(x.state, x.count);
            return (
              <button
                key={x.month}
                type="button"
                className={`p-${k.cls}${selM === x.month ? ' sel' : ''}`}
                disabled={k.cls === 'neu'}
                aria-pressed={selM === x.month}
                aria-label={`${monthName(x.month)}: ${k.label.replace('­', '')}`}
                onClick={() => select(year, selM === x.month ? null : x.month)}
              >
                {monthShort(x.month)}
                <br />
                {k.label}
              </button>
            );
          })}
        </div>
      )}
      {d && m && c && selM && (
        <div className="bmonth">
          <div className="ch">
            <h3>
              {monthName(selM)} {year} <span className={`pill p-${c.cls}`}>{c.label}</span>
            </h3>
            <span>{plural(files.length, 'Beleg', 'Belege')} vom Mandanten</span>
          </div>
          {m.state === 'fehlt' && m.note && (
            <div className="lock lk-bad">Fehlt: {m.note} · der Mandant sieht diesen Hinweis in seiner App</div>
          )}
          {m.state === 'nachtrag' && (
            <div className="lock">Neue Belege nach der Buchung · bitte prüfen und den Monat wieder als gebucht setzen</div>
          )}
          {m.state === 'nachgereicht' && (
            <div className="lock">
              Nachgereicht{m.nachgereichtOn ? ` am ${fmtDayMonth(m.nachgereichtOn)}` : ''}
              {m.note ? `: ${m.note}` : ''} · bitte prüfen und als gebucht setzen
            </div>
          )}
          <div className="bk3">
            {(Object.keys(CATEGORY_LABEL) as Category[]).map((cat) => {
              const fs = files.filter((f) => f.category === cat);
              return (
                <div key={cat}>
                  <h4>{CATEGORY_LABEL[cat]}</h4>
                  {fs.length ? (
                    fs.map((f) => (
                      <button key={f.id} type="button" onClick={() => setOpen(f.id)}>
                        {f.mime.startsWith('image/') ? '🖼 ' : istBankDatei(f) ? '🏦 ' : f.mime === 'application/xml' ? '🧾 ' : '📄 '}
                        {f.name}
                        {f.changeNote && <span className="note"> · {f.changeNote}</span>}
                        {!f.booked && (m.state === 'gebucht' || m.state === 'nachtrag') && <span className="note"> · Nachtrag</span>}
                      </button>
                    ))
                  ) : (
                    <p className="note">keine</p>
                  )}
                </div>
              );
            })}
          </div>
          {askMiss ? (
            <MissForm
              initial={m.note ?? ''}
              busy={busy}
              onCancel={() => setAskMiss(false)}
              onSubmit={(note) => setStatus('fehlt', note)}
            />
          ) : (
            <div className="bk-actions">
              {m.state !== 'gebucht' && m.state !== 'zukunft' && (
                <button type="button" className="btn sm dark" disabled={busy} onClick={() => setStatus('gebucht')}>
                  Monat als gebucht setzen
                </button>
              )}
              {m.state !== 'zukunft' && (
                <button type="button" className="btn sm" disabled={busy} onClick={() => setAskMiss(true)}>
                  {m.state === 'fehlt' ? 'Hinweis ändern' : 'Beleg fehlt'}
                </button>
              )}
              {(m.state === 'gebucht' || m.state === 'nachtrag' || m.state === 'fehlt' || m.state === 'nachgereicht') && (
                <button type="button" className="btn sm" disabled={busy} onClick={() => setStatus('reset')}>
                  Zurücksetzen
                </button>
              )}
              {files.length > 0 && (
                <a className="btn sm" href={`/api/kanzlei/belege/${clientId}/datev/${year}/${selM}`} download>
                  Für DATEV exportieren
                </a>
              )}
              {/* Kontoauszug des Monats Zeile für Zeile buchen (Seite „Buchen“) */}
              <Link className="btn sm" to={`/kanzlei/mandanten/${clientId}/buchen/${year}/${selM}`}>
                Buchen
              </Link>
            </div>
          )}
        </div>
      )}
      {open && <BelegDialog id={open} onClose={closeBeleg} />}
    </div>
  );
}

function MissForm({ initial, busy, onCancel, onSubmit }: { initial: string; busy: boolean; onCancel(): void; onSubmit(note: string): void }) {
  const [note, setNote] = useState(initial);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (note.trim()) onSubmit(note.trim());
  };
  return (
    <form className="form missform" onSubmit={submit}>
      <label>
        Was fehlt? (sieht der Mandant)
        <input
          required
          maxLength={300}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="z. B. Kontoauszug September, Rechnung Telekom"
          autoFocus
        />
      </label>
      <div className="bk-actions">
        <button className="btn sm dark" type="submit" disabled={busy}>
          Beleg fehlt setzen
        </button>
        <button className="btn sm" type="button" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
